/**
 * sublocation-geometry.js — het rekenwerk achter deellocaties, zonder kaart of netwerk.
 *
 * Los van sublocations.js zodat het in Node te testen is (dat bestand haalt Leaflet en de
 * API binnen, en die willen een browser).
 *
 * Een vlak is een open ring van [lengtegraad, breedtegraad]-paren (WGS84), net als de x/y van
 * een boring.
 *
 * Een boring kan in meer dan één deellocatie liggen: deellocaties mogen overlappen (bij
 * NS-locaties dienen de boringen voor het leidingwerk ook voor de bovengrond van het terrein),
 * en een boring op de grens van twee vlakken hoort bij allebei.
 */

/**
 * Ligt het punt in het vlak? Straalmethode: tel hoe vaak een horizontale lijn vanaf het punt
 * de rand kruist. Op de schaal van een bodemonderzoek is rekenen in graden nauwkeurig genoeg.
 *
 * Een punt precies op de rand valt hier soms binnen en soms buiten; zie inSublocation.
 */
export function pointInRing(lng, lat, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j];
    if ((yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Hoe dicht bij de rand een boring bij het automatisch toewijzen nog in het vlak telt, in meters. */
export const EDGE_TOLERANCE_M = 0.5;

const M_PER_DEG_LAT = 111_320;

/**
 * Afstand van een punt tot de rand van het vlak, in meters. Gerekend in een vlakke projectie
 * rond het punt (lengtegraad × cos(breedtegraad)), net als ringAreaM2.
 */
export function distanceToRing(lng, lat, ring) {
  const kx = M_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180);
  const ky = M_PER_DEG_LAT;
  let best = Infinity;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const ax = (ring[j][0] - lng) * kx, ay = (ring[j][1] - lat) * ky;
    const bx = (ring[i][0] - lng) * kx, by = (ring[i][1] - lat) * ky;
    const dx = bx - ax, dy = by - ay;
    const len2 = dx * dx + dy * dy;
    const t = len2 ? Math.max(0, Math.min(1, -(ax * dx + ay * dy) / len2)) : 0;
    best = Math.min(best, Math.hypot(ax + t * dx, ay + t * dy));
  }
  return best;
}

/**
 * Ligt het punt in het vlak, of binnen EDGE_TOLERANCE_M van de rand? Voor het automatisch
 * toewijzen van boringen aan een getekend vlak.
 *
 * De rand telt mee: wie een vlak tekent, klikt vaak een hoekpunt precies op een boring (de
 * boringen zijn tijdens het tekenen doorzichtig voor klikken). Met alleen de straalmethode viel
 * zo'n boring willekeurig binnen of buiten, en lag hij tussen twee aangrenzende deellocaties
 * soms in geen van beide.
 */
export function inSublocation(lng, lat, sublocation) {
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return false;
  const ring = sublocation?.geometry;
  if (!(ring?.length >= 3)) return false;
  return pointInRing(lng, lat, ring) || distanceToRing(lng, lat, ring) <= EDGE_TOLERANCE_M;
}

/** Alle deellocaties waarvan het vlak het punt bevat (met de randmarge), in de volgorde van de lijst. */
export function sublocationsAt(lng, lat, sublocations) {
  return (sublocations || []).filter((s) => inSublocation(lng, lat, s));
}

/** De eerste deellocatie waarvan het vlak het punt bevat, of null. */
export function sublocationAt(lng, lat, sublocations) {
  return sublocationsAt(lng, lat, sublocations)[0] || null;
}

/**
 * Oppervlakte van een vlak in m², afgerond op hele m². Gerekend in een vlakke projectie rond
 * het midden van het vlak: voor een terrein van een paar hectare wijkt dat minder dan een
 * promille af van de echte oppervlakte, en het is alleen een voorstel dat de adviseur ziet.
 */
export function ringAreaM2(ring) {
  if (!Array.isArray(ring) || ring.length < 3) return null;
  const R = 6371008.8;
  const rad = Math.PI / 180;
  const lat0 = (ring.reduce((s, [, lat]) => s + lat, 0) / ring.length) * rad;
  const pts = ring.map(([lng, lat]) => [lng * rad * R * Math.cos(lat0), lat * rad * R]);
  let twice = 0;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) twice += pts[j][0] * pts[i][1] - pts[i][0] * pts[j][1];
  return Math.round(Math.abs(twice) / 2);
}

/** De eerste vrije naam in de reeks DL01, DL02, ... */
export function nextSublocationName(sublocations) {
  const taken = new Set((sublocations || []).map((s) => String(s.name).toLowerCase()));
  for (let i = 1; ; i++) {
    const name = `DL${String(i).padStart(2, '0')}`;
    if (!taken.has(name.toLowerCase())) return name;
  }
}
